import { Box, Stack } from '@mui/material'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CopyToClipboardButton from '@/components/inputs/CopyToClipboardButton'
import { truncateId } from '@/utils/truncateId'

export type CopyableIdProps = {
  value: string | null | undefined
  /**
   * The complete action label, used as both the accessible name and the tooltip of the copy button,
   * so it should include the full value — nothing else shows it. Passed in whole rather than
   * composed here: interpolating a translated noun into a translated sentence hands translators a
   * fragment they cannot inflect or reorder.
   */
  copyLabel: string
  /** Shown instead of the id, which then lives only in `copyLabel` and on the clipboard. */
  displayText?: string
}

/**
 * A database entity id, reachable from a cell that shows something else.
 *
 * The displayed text is deliberately outside the button: text inside a button cannot be
 * drag-selected, and the name is what people quote in a ticket. The copying is
 * `CopyToClipboardButton`'s rather than a second implementation, which is what keeps the success
 * alert out of the try — inside it, a throw from the alert path after a successful write is
 * reported as a failed copy.
 */
export default function CopyableId({ value, copyLabel, displayText }: Readonly<CopyableIdProps>) {
  const text = displayText ?? (value ? truncateId(value) : null)

  return (
    <Stack alignItems="center" direction="row" gap={0.25}>
      {text === null ? (
        <CustomTypography color="grey.500" variant="caption">
          -
        </CustomTypography>
      ) : (
        /*
         * Plain text, no button ancestor and no `user-select` of its own: this has to stay
         * drag-selectable, which is the whole reason the button is only an icon.
         *
         * A port name can be one long unbreakable word — `CHEMOURS_EAST_WING_MAIN_METER1` — and
         * without these it keeps its `min-width: auto` and runs straight out of the cell and across
         * the next column. `anywhere` rather than `break-all` so ordinary names still break at their
         * spaces and hyphens first, and only a word with nowhere to break is cut mid-word.
         */
        <CustomTypography
          color={displayText === undefined ? 'grey.500' : undefined}
          sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
          variant={displayText === undefined ? 'caption' : 'inherit'}
        >
          {text}
        </CustomTypography>
      )}
      {/*
       * Nothing to copy means no control, rather than a button that would do nothing.
       *
       * `flexShrink: 0` sits on a wrapper because the shared button takes no style of its own. It
       * is what holds the control at 36px now that the label beside it gives way: the label breaks
       * anywhere rather than pushing, so without this the flex row would take its space from the
       * button instead.
       */}
      {value ? (
        <Box sx={{ flexShrink: 0 }}>
          <CopyToClipboardButton actionLabel={copyLabel} value={value} />
        </Box>
      ) : null}
    </Stack>
  )
}
